import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { QualityBadge } from "@/components/QualityBadge";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { ChangeMobileDialog, ChangeHistoryDialog } from "@/components/ControlledEdit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Phone, MessageCircle, Search } from "lucide-react";
import { useCallTracker } from "@/components/CallTracker";

const STATUS = ["new_lead", "contacted", "interested", "documents_pending", "documents_received", "submitted", "under_review", "approved", "rejected", "disbursed", "closed"];
const EMP = ["Salaried", "Self-Employed", "Business Owner", "Other"];

export default function LoanLeads() {
  const { trackCall } = useCallTracker();
  const { hasPerm } = useAuth();
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [status, setStatus] = useState("all");
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const params = { ...(q ? { q } : {}), ...(status !== "all" ? { status } : {}) };
  const { items, total, loading, page, setPage, refetch } = useList("/loan/leads", params);
  useEffect(() => {
    api.get("/loan/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);
  const staff = users.filter((u) => ["telecaller", "team_leader", "sales_manager", "back_office"].includes(u.role));
  const create = async (v) => { await api.post("/loan/leads", v); refetch(); };

  const fields = [
    { name: "name", label: "Customer Name", required: true },
    { name: "mobile", label: "Mobile", required: true },
    { name: "email", label: "Email", type: "email" },
    { name: "city", label: "City" }, { name: "state", label: "State" }, { name: "pincode", label: "PIN Code" },
    { name: "loan_type", label: "Loan Type", type: "select", required: true, options: products.map((p) => ({ value: p.name, label: p.name })) },
    { name: "required_amount", label: "Required Amount (₹)", type: "number" },
    { name: "employment_type", label: "Employment Type", type: "select", options: EMP },
    { name: "monthly_income", label: "Monthly Income / Turnover (₹)", type: "number" },
    { name: "existing_obligations", label: "Existing Loan Obligations (₹)", type: "number" },
    { name: "lead_source", label: "Lead Source", type: "select", options: ["website", "referral", "partner", "publisher", "manual"].map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "assigned_to", label: "Assign To (blank = auto by area rule)", type: "select", options: staff.map((u) => ({ value: u.id, label: u.name })) },
    { name: "priority", label: "Priority", type: "select", options: ["high", "medium", "low"].map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "remarks", label: "Remarks", type: "textarea", full: true },
  ];
  const columns = [
    { key: "loan_lead_id", header: "Lead ID", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.loan_lead_id}</span> },
    { key: "name", header: "Name", render: (r) => <div><div className="font-semibold">{r.name}</div><div className="font-mono text-xs text-muted-foreground">{r.mobile}</div></div> },
    { key: "loan_type", header: "Loan Type", render: (r) => <span className="text-sm">{titleCase(r.loan_type)}</span> },
    { key: "required_amount", header: "Amount", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular font-semibold">{INR(r.required_amount)}</span> },
    { key: "employment_type", header: "Employment", render: (r) => <span className="text-sm text-muted-foreground">{r.employment_type || "—"}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "quality", header: "Quality", render: (r) => <QualityBadge quality={r.quality} /> },
    { key: "actions", header: "", render: (r) => (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" asChild data-testid="loan-lead-call-button"><a href={`tel:${r.mobile}`} onClick={() => trackCall({ business: "loan", lead_id: r.id, name: r.name, mobile: r.mobile })}><Phone className="h-4 w-4" /></a></Button>
        <WhatsAppButton lead={r} testId="loan-lead-whatsapp-button" />
        {hasPerm("leads:edit_mobile") && <ChangeMobileDialog lead={r} leadType="loan" onDone={refetch} />}
        {(hasPerm("audit:view") || hasPerm("leads:edit_mobile")) && <ChangeHistoryDialog entityId={r.id} />}
      </div>
    ) },
  ];
  return (
    <div>
      <PageHeader title="Loan Leads" subtitle={`${total} loan leads`} icon={Users}
        actions={hasPerm("loans:add") && (
          <FormDialog title="New Loan Lead" description="Financial details are restricted to authorized roles." fields={fields} onSubmit={create} submitLabel="Create Lead" testId="create-loan-lead-dialog"
            trigger={<Button data-testid="create-loan-lead-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Loan Lead</Button>} />
        )} />
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search name, mobile, lead ID..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="loan-lead-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-48" data-testid="loan-lead-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUS.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent></Select>
      </div>
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="loan-lead-table-row" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
